import { useState, useEffect, useRef } from 'react';
import * as qaSubmissionService from '../../../services/qaSubmissionService';
import * as profileListingService from '../../../services/profileListingService';
import type { QaModuleData, QaAnswer } from '../../../services/qaSubmissionService';

interface QaSubmissionDetailProps {
  onBack: () => void;
  darkMode: boolean;
}

export default function QaSubmissionDetail({ onBack, darkMode }: QaSubmissionDetailProps) {
  const [activeTab, setActiveTab] = useState<'new' | 'existing'>('new');
  
  // API State
  const [moduleData, setModuleData] = useState<QaModuleData | null>(null);
  const [loadingData, setLoadingData] = useState(true);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generationId, setGenerationId] = useState<string | null>(null);
  const [generatedAnswers, setGeneratedAnswers] = useState<QaAnswer[]>([]);
  const [answersGenerated, setAnswersGenerated] = useState(false);
  const [drawerAnswer, setDrawerAnswer] = useState<QaAnswer | null>(null);
  const [qaCopiedField, setQaCopiedField] = useState<string | null>(null);
  const [editAnswerText, setEditAnswerText] = useState('');
  const [editQuestionText, setEditQuestionText] = useState('');
  const [isSavingEdit, setIsSavingEdit] = useState(false);
  const [selectedKeyword, setSelectedKeyword] = useState<string>('');
  const [activeGroupKeyword, setActiveGroupKeyword] = useState<string | null>(null);
  
  // UI State
  const [showOutput, setShowOutput] = useState(false);
  // Audit / scan state
  const [isScanning, setIsScanning] = useState(false);
  const [scanId, setScanId] = useState<string | null>(null);
  const [scanAssets, setScanAssets] = useState<any[]>([]);
  const [qaAuditRun, setQaAuditRun] = useState(false);

  // Active Selection & Asset Checklist State
  const [selectedAnswer, setSelectedAnswer] = useState<QaAnswer | null>(null);
  const [isMarkingComplete, setIsMarkingComplete] = useState(false);
  const [markCompleteSuccess, setMarkCompleteSuccess] = useState(false);

  // Synchronize selectedAnswer with refreshed generatedAnswers
  useEffect(() => {
    async function loadData() {
      try {
        const data = await qaSubmissionService.getModuleData();
        setModuleData(data);
        if (data.is_new_website) {
          setActiveTab('new');
        } else {
          setActiveTab('existing');
        }
        
        const existingAnswers = await qaSubmissionService.getAllAnswers();
        if (existingAnswers.length > 0) {
          setGeneratedAnswers(existingAnswers);
          setAnswersGenerated(true);
          setQaAuditRun(true);
        }
      } catch (err) {
        console.error('Failed to load QA submission module data', err);
      } finally {
        setLoadingData(false);
      }
    }
    loadData();
  }, []);

  useEffect(() => {
    if (generatedAnswers.length > 0) {
      const currentGroupAnswers = generatedAnswers.filter(a => a.keyword === activeGroupKeyword);
      if (currentGroupAnswers.length > 0) {
        if (!selectedAnswer || selectedAnswer.keyword !== activeGroupKeyword) {
          const valid = currentGroupAnswers.find(a => a.status !== 'FAILED');
          setSelectedAnswer(valid || currentGroupAnswers[0]);
        } else {
          const updated = currentGroupAnswers.find(a => a.id === selectedAnswer.id);
          if (updated) {
            setSelectedAnswer(updated);
          }
        }
      } else {
        setSelectedAnswer(null);
      }
    } else {
      setSelectedAnswer(null);
    }
  }, [generatedAnswers, activeGroupKeyword]);

  const handleMarkCompletePerAnswer = async () => {
    if (!selectedAnswer || !selectedAnswer.platform_name) return;
    setIsMarkingComplete(true);
    try {
      const updated = await qaSubmissionService.updateAnswer(selectedAnswer.id, {
        published_platforms: [selectedAnswer.platform_name]
      });
      
      setGeneratedAnswers(prev => prev.map(a => a.id === updated.id ? updated : a));
      setSelectedAnswer(updated);
      setMarkCompleteSuccess(true);
      setTimeout(() => setMarkCompleteSuccess(false), 3000);
    } catch (err) {
      console.error('Error marking complete for Q&A answer:', err);
    } finally {
      setIsMarkingComplete(false);
    }
  };

  const cardBg = darkMode ? 'bg-gray-800' : 'bg-white';
  const borderColor = darkMode ? 'border-gray-700' : 'border-gray-200';
  const textPrimary = darkMode ? 'text-white' : 'text-gray-900';
  const textSecondary = darkMode ? 'text-gray-400' : 'text-gray-500';

  useEffect(() => {
    async function loadData() {
      try {
        const data = await qaSubmissionService.getModuleData();
        setModuleData(data);
        if (data.is_new_website) {
          setActiveTab('new');
        } else {
          setActiveTab('existing');
        }
        
        // Also fetch any previously generated answers
        const existingAnswers = await qaSubmissionService.getAllAnswers();
        if (existingAnswers.length > 0) {
          setGeneratedAnswers(existingAnswers);
          setAnswersGenerated(true);
          setQaAuditRun(true);
          setShowOutput(true);
        }
      } catch (err) {
        console.error('Failed to load QA submission module data', err);
      } finally {
        setLoadingData(false);
      }
    }
    loadData();
  }, []);

  useEffect(() => {
    let interval: number | undefined;
    if (generationId && isGenerating) {
      interval = window.setInterval(async () => {
        try {
          const items = await qaSubmissionService.getGeneratedAnswers(generationId);
          setGeneratedAnswers(prev => {
            const others = prev.filter(a => a.generation_id !== generationId);
            return [...others, ...items];
          });
          
          // Count how many target platforms there are (top 6)
          const targetCount = (moduleData?.platforms || []).slice(0, 6).length;
          
          const allDone = items.length === targetCount && items.every(item => item.status !== 'GENERATING');
          if (allDone) {
            setIsGenerating(false);
            setAnswersGenerated(true);
          }
        } catch (err) {
          console.error('Error polling answers', err);
        }
      }, 3000);
    }
    return () => {
      if (interval) window.clearInterval(interval);
    };
  }, [generationId, isGenerating]);

  const generateAnswersAction = async () => {
    if (!moduleData) return;
    setGenerationId(null); // Clear old generationId immediately
    setIsGenerating(true);
    // Remove existing answers for the currently selected keyword so loaders show during regeneration
    setGeneratedAnswers(prev => prev.filter(a => a.keyword !== selectedKeyword));
    setActiveGroupKeyword(selectedKeyword);
    
    try {
      // Generate only for top 6 platforms as requested
      const platformNames = moduleData.platforms
        .sort((a, b) => b.da_score - a.da_score)
        .slice(0, 6)
        .map(p => p.platform_name);
      
      const res = await qaSubmissionService.generateContent(platformNames, selectedKeyword || undefined);
      setGenerationId(res.generation_id);
    } catch (err) {
      console.error('Error triggering generation', err);
      setIsGenerating(false);
    }
  };

  const handleGenerateClick = () => {
    if (!showOutput && answersGenerated) {
      setShowOutput(true);
    } else {
      setShowOutput(true);
      generateAnswersAction();
    }
  };

  const openDrawer = (answer: QaAnswer) => {
    setDrawerAnswer(answer);
    setEditAnswerText(answer.answer || '');
    setEditQuestionText(answer.question || '');
  };

  const saveDrawerEdit = async () => {
    if (!drawerAnswer) return;
    setIsSavingEdit(true);
    try {
      const updated = await qaSubmissionService.updateAnswer(drawerAnswer.id, {
        answer: editAnswerText,
        question: editQuestionText
      });
      setDrawerAnswer(updated);
      setGeneratedAnswers(prev => prev.map(a => a.id === updated.id ? updated : a));
    } catch (err) {
      console.error('Failed to update answer:', err);
    } finally {
      setIsSavingEdit(false);
    }
  };

  const getPriorityColor = (da: number) => {
    if (da >= 80) return darkMode ? 'bg-blue-900/40 text-blue-300' : 'bg-blue-100 text-blue-700';
    if (da >= 60) return darkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-500';
    return darkMode ? 'bg-gray-800 text-gray-500' : 'bg-gray-50 text-gray-400';
  };

  const getPriorityLabel = (da: number) => {
    if (da >= 80) return 'high';
    if (da >= 60) return 'medium';
    return 'low';
  };

  // Polling for Scan
  useEffect(() => {
    let interval: number | undefined;
    if (scanId && isScanning) {
      interval = window.setInterval(async () => {
        try {
          const res = await profileListingService.getScanStatus(scanId);
          if (res.status === 'COMPLETED') {
            setIsScanning(false);
            setScanAssets(res.result?.assets || []);
            setQaAuditRun(true);
          } else if (res.status === 'FAILED') {
            setIsScanning(false);
            setQaAuditRun(true);
            setScanAssets([]);
          }
        } catch (err) {
          console.error('Error polling scan status', err);
        }
      }, 3000);
    }
    return () => {
      if (interval) window.clearInterval(interval);
    };
  }, [scanId, isScanning]);

  const runQaAudit = async () => {
    setIsScanning(true);
    setQaAuditRun(false);
    try {
      const userId = localStorage.getItem('user_id');
      if (!userId) {
        console.error('No user ID found for audit');
        setIsScanning(false);
        return;
      }
      // Reusing the same endpoint as ProfileListing / DirectorySubmission, with module=qa_submission
      const res = await profileListingService.initiateScan(userId, 'qa_submission');
      setScanId(res.scan_id);
    } catch (err) {
      console.error('Failed to initiate scan', err);
      setIsScanning(false);
    }
  };

  const fallbackCopy = (text: string) => {
    const textArea = document.createElement("textarea");
    textArea.value = text;
    textArea.style.top = "0";
    textArea.style.left = "0";
    textArea.style.position = "fixed";
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    try { document.execCommand("copy"); } catch (e) {}
    document.body.removeChild(textArea);
  };

  const handleQaCopyField = (text: string, field: string) => {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(text).catch(() => fallbackCopy(text));
    } else {
      fallbackCopy(text);
    }
    setQaCopiedField(field);
    setTimeout(() => setQaCopiedField(null), 2000);
  };

  const handleQaCopyAll = (ans: QaAnswer) => {
    const text = [
      `Question: ${ans.question || ''}`,
      `Answer: ${ans.answer || ''}`,
      `Keyword: ${ans.keyword || ''}`,
    ].join('\n\n');
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(text).catch(() => fallbackCopy(text));
    } else {
      fallbackCopy(text);
    }
    setQaCopiedField('all');
    setTimeout(() => setQaCopiedField(null), 2000);
  };

  if (loadingData) {
    return (
      <div className="flex items-center justify-center p-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  // Use only Top 6 platforms
  const platforms = (moduleData?.platforms || []).sort((a, b) => b.da_score - a.da_score).slice(0, 6);
  const project_context = moduleData?.project_context;
  
  const napPreview = {
    name: project_context?.brand || 'Not set',
    url: project_context?.website_url || 'Not set',
    category: project_context?.primary_business || 'Business',
    keywords: (project_context?.keywords || []).join(', ') || 'Not set',
  };

  return (
    <div className="space-y-6">
      {/* Back Button */}
      <div className="flex items-center gap-4">
        <button
          onClick={onBack}
          className="flex items-center space-x-2 text-sm font-medium text-gray-500 hover:text-blue-600 dark:text-gray-400 dark:hover:text-blue-400 transition-colors cursor-pointer"
        >
          <i className="ri-arrow-left-line"></i>
          <span>Back to Off-Page Content</span>
        </button>
      </div>

      {/* Header */}
      <div className="flex items-start gap-4">
        <div className="w-12 h-12 rounded-xl bg-blue-50 dark:bg-blue-900/30 flex items-center justify-center flex-shrink-0 border border-blue-200 dark:border-blue-800">
          <i className="ri-question-answer-line text-blue-600 dark:text-blue-400 text-xl"></i>
        </div>
        <div>
          <h2 className="text-xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            Q&A Submission
            <div className="relative group cursor-pointer inline-flex items-center z-40 ml-2">
              <i
                className="ri-information-line text-lg text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
                onClick={(e) => {
                  e.stopPropagation();
                  const t = document.getElementById('qa-submission-tooltip');
                  if (t) {
                    t.classList.toggle('opacity-0');
                    t.classList.toggle('opacity-100');
                    setTimeout(() => {
                      t.classList.add('opacity-0');
                      t.classList.remove('opacity-100');
                    }, 4000);
                  }
                }}
              ></i>
              <div
                id="qa-submission-tooltip"
                className="absolute left-0 top-full mt-2 w-80 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-left p-4 rounded-xl shadow-xl opacity-0 pointer-events-none transition-opacity z-50 leading-relaxed"
              >
                <p className="text-sm font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                  <i className="ri-error-warning-line text-blue-600 dark:text-blue-400"></i>
                  Prerequisite Inputs
                </p>
                <ul className="space-y-2">
                  <li className="flex items-start gap-2 text-xs text-gray-600 dark:text-gray-400">
                    <i className="ri-checkbox-circle-fill text-teal-500 mt-0.5"></i>
                    <span>Brand Name &amp; URL</span>
                  </li>
                  <li className="flex items-start gap-2 text-xs text-gray-600 dark:text-gray-400">
                    <i className="ri-checkbox-circle-fill text-teal-500 mt-0.5"></i>
                    <span>SEO Keywords</span>
                  </li>
                  <li className="flex items-start gap-2 text-xs text-gray-600 dark:text-gray-400">
                    <i className="ri-checkbox-circle-fill text-teal-500 mt-0.5"></i>
                    <span>Business category and expertise</span>
                  </li>
                </ul>
              </div>
            </div>
          </h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Generate expert answers on Q&A sites to build authority and link equity.</p>
        </div>
      </div>

      {/* Tab Switcher */}
      <div className="inline-flex bg-gray-100 dark:bg-gray-800 rounded-full p-1">
        <button
          onClick={() => setActiveTab('new')}
          disabled={!moduleData?.is_new_website}
          className={`px-5 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap ${
            !moduleData?.is_new_website ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
          } ${
            activeTab === 'new'
              ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm'
              : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
          }`}
        >
          <i className="ri-add-circle-line mr-1.5"></i>
          New Website
        </button>
        <button
          onClick={() => setActiveTab('existing')}
          disabled={moduleData?.is_new_website}
          className={`px-5 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap ${
            moduleData?.is_new_website ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
          } ${
            activeTab === 'existing'
              ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm'
              : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
          }`}
        >
          <i className="ri-refresh-line mr-1.5"></i>
          Existing Website
        </button>
      </div>

      {/* Audit Section for Existing Website Only */}
      {activeTab === 'existing' && (
        <div className={`${cardBg} rounded-xl border ${borderColor} p-5`}>
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-4">
            <div>
              <h3 className={`text-sm font-semibold ${textPrimary} flex items-center gap-2`}>
                <i className="ri-radar-line text-blue-600 dark:text-blue-400"></i>
                Q&A Presence Scanner
              </h3>
              <p className={`text-xs ${textSecondary} mt-0.5`}>
                Scans platforms like Quora and Reddit for existing mentions of your brand.
              </p>
            </div>
            
            {!qaAuditRun && !isScanning ? (
              <button
                onClick={runQaAudit}
                className="px-5 py-2.5 bg-white border border-gray-200 dark:bg-gray-800 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 text-sm font-medium rounded-lg transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-search-eye-line mr-2"></i>
                Run Q&A Scan
              </button>
            ) : isScanning ? (
              <button disabled className="px-5 py-2.5 bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400 text-sm font-medium rounded-lg opacity-70 flex items-center cursor-not-allowed">
                <i className="ri-loader-4-line animate-spin mr-2"></i>
                Scanning...
              </button>
            ) : (
              <div className="flex items-center gap-3">
                <span className="px-3 py-1.5 text-xs font-medium rounded-full bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-800">
                  <i className="ri-check-line mr-1"></i>
                  Scan Completed
                </span>
                <button
                  onClick={runQaAudit}
                  className="px-4 py-1.5 text-xs font-medium rounded-lg transition-colors whitespace-nowrap cursor-pointer bg-white dark:bg-gray-800 border border-blue-600 dark:border-blue-500 text-blue-600 dark:text-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/20"
                >
                  <i className="ri-refresh-line mr-1"></i>Re-scan
                </button>
              </div>
            )}
          </div>

          {qaAuditRun && (
            <div className="space-y-4">
              {scanAssets.length === 0 ? (
                <div className="flex flex-col items-center justify-center p-8 mt-4 border border-dashed border-gray-300 dark:border-gray-700 rounded-lg bg-gray-50 dark:bg-gray-800/50">
                  <div className="w-12 h-12 rounded-full bg-blue-50 dark:bg-blue-900/30 flex items-center justify-center mb-3">
                    <i className="ri-information-line text-blue-600 dark:text-blue-400 text-xl"></i>
                  </div>
                  <p className="text-sm font-medium text-gray-900 dark:text-white mb-1">No Existing Q&A Citations Found</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 text-center max-w-md">
                    We scanned the target Q&A platforms but couldn't find any existing threads mentioning your brand.
                  </p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50 dark:bg-gray-700/50">
                    <tr>
                      <th className="text-left py-3 px-4 text-xs font-semibold text-gray-600 dark:text-gray-300">Platform</th>
                      <th className="text-left py-3 px-4 text-xs font-semibold text-gray-600 dark:text-gray-300">Thread URL</th>
                      <th className="text-left py-3 px-4 text-xs font-semibold text-gray-600 dark:text-gray-300">Brand Cited</th>
                      <th className="text-left py-3 px-4 text-xs font-semibold text-gray-600 dark:text-gray-300">Ownership</th>
                      <th className="text-left py-3 px-4 text-xs font-semibold text-gray-600 dark:text-gray-300">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                      {scanAssets.map((issue, idx) => (
                        <tr key={idx} className="hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors">
                        <td className="py-3 px-4">
                          <span className="text-sm font-medium text-gray-900 dark:text-white">{issue.platform}</span>
                        </td>
                        <td className="py-3 px-4">
                          <a href={issue.url} target="_blank" rel="noreferrer" className="text-sm text-blue-600 dark:text-blue-400 hover:underline max-w-[200px] truncate block">
                            {issue.url}
                          </a>
                        </td>
                        <td className="py-3 px-4">
                          <span className="text-xs font-medium text-gray-600 dark:text-gray-400">Yes</span>
                        </td>
                        <td className="py-3 px-4">
                          <span className="text-sm text-gray-700 dark:text-gray-300">{issue.ownership_status || 'Unknown'}</span>
                        </td>
                        <td className="py-3 px-4">
                          <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                            issue.status === 'Live'
                              ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400' 
                              : 'bg-gray-50 text-gray-700 dark:bg-gray-900/30 dark:text-gray-400'
                          }`}>
                            {issue.status || 'Live'}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Pre-Filled Data Preview */}
      <div className={`${cardBg} rounded-xl border ${borderColor} p-5`}>
        <h3 className={`text-sm font-semibold ${textPrimary} mb-4 flex items-center gap-2`}>
          <i className="ri-draft-line text-blue-600 dark:text-blue-400"></i>
          Brand Q&A Profile
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
          <div>
            <span className="text-xs text-gray-400 dark:text-gray-500">Business Name</span>
            <p className="text-sm font-medium text-gray-900 dark:text-white mt-1">{napPreview.name}</p>
          </div>
          <div>
            <span className="text-xs text-gray-400 dark:text-gray-500">Website</span>
            <p className="text-sm font-medium text-blue-600 dark:text-blue-400 mt-1">{napPreview.url}</p>
          </div>
          <div>
            <span className="text-xs text-gray-400 dark:text-gray-500">Category</span>
            <p className="text-sm font-medium text-gray-900 dark:text-white mt-1">{napPreview.category}</p>
          </div>
        </div>
      </div>

      {/* Keyword Selection */}
      <div className={`${cardBg} rounded-xl border ${borderColor} p-5`}>
        <h3 className={`text-sm font-semibold ${textPrimary} flex items-center gap-2 mb-1`}>
          <i className="ri-key-line text-blue-600 dark:text-blue-400"></i>
          Select Target Keyword
        </h3>
        <p className={`text-xs ${textSecondary} mb-4`}>
          Choose the primary keyword to search for relevant Q&A threads and generate answers.
        </p>
        
        {(project_context?.keywords || []).length > 0 ? (
          <div className="flex flex-wrap gap-2">
            {(project_context?.keywords || []).map((kw: string, idx: number) => (
              <button
                key={idx}
                onClick={() => setSelectedKeyword(kw)}
                className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                  selectedKeyword === kw
                    ? 'bg-blue-600 text-white shadow-sm'
                    : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-700 hover:border-blue-400 dark:hover:border-blue-500'
                }`}
              >
                {kw}
              </button>
            ))}
          </div>
        ) : (
          <p className="text-sm text-gray-500 dark:text-gray-400 italic">No keywords found. Add keywords in your project context.</p>
        )}
      </div>

      {/* Output / Generated Assets */}
      <div className={`${cardBg} rounded-xl border ${borderColor} p-5 space-y-6`}>
        <div className="flex items-center justify-between">
          <div>
            <h3 className={`text-sm font-semibold ${textPrimary}`}>Generated Q&A Answers</h3>
            <p className={`text-xs ${textSecondary} mt-0.5`}>
              Click a Q&A answer asset to view details and track submission checklists below
            </p>
          </div>
          <div className="flex items-center gap-3">
            {!isGenerating ? (
              <button
                onClick={handleGenerateClick}
                className="px-5 py-2.5 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className={(answersGenerated && showOutput) ? "ri-refresh-line mr-2" : "ri-magic-line mr-2"}></i>
                {(answersGenerated && showOutput) ? "Regenerate" : "Generate Answers"}
              </button>
            ) : (
              <button disabled className="px-5 py-2.5 bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400 text-sm font-medium rounded-lg opacity-70 flex items-center cursor-not-allowed">
                <i className="ri-loader-4-line animate-spin mr-2"></i>
                Generating...
              </button>
            )}
          </div>
        </div>

        {showOutput && (
          <>
            {(answersGenerated || generatedAnswers.length > 0 || isGenerating) && (
              <div className="space-y-6">
                
                {/* Keyword Groups */}
                {(() => {
                  const uniqueKeywords = Array.from(new Set(generatedAnswers.map(a => a.keyword).filter(Boolean))) as string[];
                  if (activeGroupKeyword && !uniqueKeywords.includes(activeGroupKeyword)) {
                    uniqueKeywords.unshift(activeGroupKeyword);
                  }
                  
                  if (uniqueKeywords.length === 0) return null;

                  return (
                    <div className="flex flex-wrap gap-2 pb-2">
                      {uniqueKeywords.map((kw) => (
                        <button
                          key={kw}
                          onClick={() => setActiveGroupKeyword(kw)}
                          className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all cursor-pointer ${
                            activeGroupKeyword === kw
                              ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300 border border-blue-300 dark:border-blue-700 shadow-sm'
                              : 'bg-white text-gray-600 dark:bg-gray-800 dark:text-gray-400 border border-gray-200 dark:border-gray-700 hover:border-blue-300 dark:hover:border-blue-500'
                          }`}
                        >
                          <i className="ri-key-2-line mr-2 opacity-70"></i>
                          {kw}
                        </button>
                      ))}
                    </div>
                  );
                })()}

                {/* Generated Q&A Cards Grid */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {platforms.map((dir) => {
                    const answer = generatedAnswers.find(l => l.platform_name === dir.platform_name && l.keyword === activeGroupKeyword);
                    const isSelected = selectedAnswer?.id === answer?.id || selectedAnswer?.platform_name === dir.platform_name;
                    const isCurrentlyGenerating = isGenerating && activeGroupKeyword === selectedKeyword && (!answer || answer.status === 'GENERATING');
                    const hasAnswer = !!answer;
                    const isClickable = hasAnswer && !isCurrentlyGenerating;

                    return (
                      <div
                        key={dir.platform_name}
                        onClick={() => { if (isClickable) setSelectedAnswer(answer); }}
                        className={`group relative ${isClickable ? 'cursor-pointer' : 'cursor-default'} rounded-xl p-4 border transition-all ${
                          isSelected && isClickable
                            ? 'border-blue-500 ring-2 ring-blue-100 dark:ring-blue-900/30 bg-blue-50/20 dark:bg-blue-900/10'
                            : hasAnswer
                              ? 'border-gray-200 dark:border-gray-700 hover:border-blue-300 bg-white dark:bg-gray-800 shadow-sm'
                              : 'border-gray-100 dark:border-gray-800 bg-gray-50/20 dark:bg-gray-900/5 opacity-55'
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
                            isCurrentlyGenerating 
                              ? 'bg-gray-100 dark:bg-gray-700 animate-pulse' 
                              : hasAnswer 
                                ? 'bg-blue-50 dark:bg-blue-900/30' 
                                : 'bg-gray-150 dark:bg-gray-800'
                          }`}>
                            {isCurrentlyGenerating ? (
                              <i className="ri-loader-4-line animate-spin text-gray-400 text-lg"></i>
                            ) : (
                              <i className={`ri-question-answer-line text-lg ${hasAnswer ? 'text-blue-600 dark:text-blue-400' : 'text-gray-400 dark:text-gray-500'}`}></i>
                            )}
                          </div>
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center justify-between gap-1 mb-1">
                              <span className={`text-xs font-bold truncate ${hasAnswer ? 'text-gray-900 dark:text-white' : 'text-gray-400 dark:text-gray-500'}`}>
                                {dir.platform_name}
                              </span>
                              {!isCurrentlyGenerating && answer && (
                                answer.published_platforms && answer.published_platforms.length > 0 ? (
                                  <span className="px-1.5 py-0.5 text-[10px] font-bold rounded bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400 flex items-center gap-0.5">
                                    <i className="ri-check-line text-xs" /> Completed
                                  </span>
                                ) : (
                                  <span className="px-1.5 py-0.5 text-[10px] font-medium rounded bg-amber-50 text-amber-600 dark:bg-amber-900/20 dark:text-amber-400">
                                    Pending
                                  </span>
                                )
                              )}
                            </div>
                            {isCurrentlyGenerating ? (
                              <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-2/3 animate-pulse mt-2"></div>
                            ) : (
                              <p className={`text-[11px] line-clamp-none whitespace-normal break-words mt-1 leading-snug ${hasAnswer ? 'text-gray-500 dark:text-gray-400' : 'text-gray-400 dark:text-gray-500 italic'}`}>
                                {answer?.question || (hasAnswer ? 'Answer ready' : 'Not generated')}
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Active Q&A Dedicated Asset View */}
                {selectedAnswer && (
                  <div className="pt-4 border-t border-gray-100 dark:border-gray-700">
                    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm overflow-hidden">
                      {/* Header */}
                      <div className="flex flex-col sm:flex-row sm:items-center justify-between p-5 border-b border-gray-100 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-800/50 gap-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-lg bg-blue-100 dark:bg-blue-900/40 flex items-center justify-center flex-shrink-0">
                            <i className="ri-question-answer-fill text-blue-600 dark:text-blue-400 text-lg"></i>
                          </div>
                          <div>
                            <h3 className="font-bold text-gray-900 dark:text-white text-base">
                              {selectedAnswer.platform_name} Q&A Details
                            </h3>
                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                              Review the generated response tailored for this specific thread.
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          {(() => {
                            const p = moduleData?.platforms?.find(p => p.platform_name === selectedAnswer.platform_name);
                            const threadUrl = selectedAnswer.question_url || (p?.platform_url ? `https://${p.platform_url}` : `https://${selectedAnswer.platform_name.toLowerCase().replace(/\s+/g, '') + '.com'}`);
                            return (
                              <a
                                href={threadUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="px-4 py-2 text-xs font-semibold rounded-lg bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors flex items-center gap-1.5"
                              >
                                <i className="ri-external-link-line" /> Visit Thread
                              </a>
                            );
                          })()}
                          <div className="flex items-center gap-2">
                            {markCompleteSuccess && (
                              <span className="text-xs font-bold text-emerald-600 dark:text-emerald-400 flex items-center gap-1">
                                <i className="ri-checkbox-circle-fill" /> Saved!
                              </span>
                            )}
                            <button
                              onClick={handleMarkCompletePerAnswer}
                              disabled={isMarkingComplete}
                              className="px-4 py-2 text-xs font-bold rounded-lg bg-blue-600 hover:bg-blue-700 text-white transition-all shadow-sm cursor-pointer border-none flex items-center gap-1.5"
                            >
                              {isMarkingComplete ? (
                                <><i className="ri-loader-4-line animate-spin" /> Saving...</>
                              ) : (
                                <><i className="ri-checkbox-circle-line" /> Mark as Complete</>
                              )}
                            </button>
                          </div>
                        </div>
                      </div>

                      {/* Content */}
                      <div className="p-6 space-y-6">
                        {/* Question Block */}
                        <div>
                          <div className="flex items-center gap-2 mb-2">
                            <span className="text-[10px] uppercase font-bold text-gray-500 dark:text-gray-400 tracking-wider">
                              Original Question
                            </span>
                          </div>
                          <h4 className="text-lg font-semibold text-gray-900 dark:text-gray-100 leading-snug">
                            {selectedAnswer.question || '—'}
                          </h4>
                        </div>

                        {/* Answer Block */}
                        <div className="relative">
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-[10px] uppercase font-bold text-blue-600 dark:text-blue-400 tracking-wider flex items-center gap-1.5">
                              <i className="ri-sparkling-line" /> AI Generated Response
                            </span>
                            <div className="flex items-center gap-3">
                              <button
                                onClick={() => handleQaCopyField(selectedAnswer.answer || '', 'detail_answer')}
                                className="text-xs font-semibold text-blue-600 dark:text-blue-400 hover:underline flex items-center gap-1 cursor-pointer bg-transparent border-none"
                              >
                                <i className={qaCopiedField === 'detail_answer' ? 'ri-check-line text-emerald-500' : 'ri-file-copy-line'} />
                                {qaCopiedField === 'detail_answer' ? 'Copied!' : 'Copy Answer'}
                              </button>
                              <button
                                onClick={() => openDrawer(selectedAnswer)}
                                className="text-xs font-semibold text-blue-600 dark:text-blue-400 hover:underline flex items-center gap-1 cursor-pointer bg-transparent border-none"
                              >
                                <i className="ri-edit-box-line" /> Edit Answer
                              </button>
                            </div>
                          </div>
                          <div className="p-5 bg-blue-50/50 dark:bg-blue-900/10 border border-blue-100 dark:border-blue-900/30 rounded-xl">
                            <p className="text-gray-700 dark:text-gray-300 text-sm leading-relaxed whitespace-pre-wrap">
                              {selectedAnswer.answer || '—'}
                            </p>
                          </div>
                        </div>

                        {/* Metadata Strip */}
                        <div className="flex flex-wrap items-center gap-4 pt-2">
                          <div className="flex items-center gap-2 px-3 py-1.5 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-100 dark:border-gray-700">
                            <i className="ri-file-text-line text-gray-400" />
                            <span className="text-xs text-gray-600 dark:text-gray-400">
                              <strong className="text-gray-900 dark:text-gray-200">{selectedAnswer.word_count || 0}</strong> words
                            </span>
                          </div>
                          <div className="flex items-center gap-2 px-3 py-1.5 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-100 dark:border-gray-700">
                            <i className="ri-key-2-line text-gray-400" />
                            <span className="text-xs text-gray-600 dark:text-gray-400">
                              Keyword: <strong className="text-purple-600 dark:text-purple-400">{selectedAnswer.keyword || '—'}</strong>
                            </span>
                          </div>
                          {(() => {
                            const p = moduleData?.platforms?.find(p => p.platform_name === selectedAnswer.platform_name);
                            if (p?.da_score) {
                              return (
                                <div className="flex items-center gap-2 px-3 py-1.5 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-100 dark:border-gray-700">
                                  <i className="ri-bar-chart-grouped-line text-gray-400" />
                                  <span className="text-xs text-gray-600 dark:text-gray-400">
                                    Domain Authority: <strong className="text-blue-600 dark:text-blue-400">{p.da_score}</strong>
                                  </span>
                                </div>
                              );
                            }
                            return null;
                          })()}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>

      {/* Detail Drawer */}
      {drawerAnswer && (
        <>
          <div 
            className="fixed inset-0 bg-black/40 dark:bg-black/60 z-40 backdrop-blur-sm transition-opacity"
            onClick={() => setDrawerAnswer(null)}
          />
          <div className={`fixed inset-y-0 right-0 w-full sm:w-[500px] ${cardBg} shadow-2xl z-50 transform transition-transform duration-300 flex flex-col border-l ${borderColor} overflow-hidden`}>
            <div className={`px-6 py-4 border-b ${borderColor} flex items-center justify-between bg-gray-50 dark:bg-gray-800/80`}>
              <div>
                <h2 className={`text-lg font-bold ${textPrimary} flex items-center gap-2`}>
                  {drawerAnswer.platform_name} Answer
                </h2>
                <p className={`text-xs ${textSecondary} mt-0.5`}>
                  Review or edit the generated answer.
                </p>
              </div>
              <button 
                onClick={() => setDrawerAnswer(null)}
                className={`p-2 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 ${textSecondary} transition-colors cursor-pointer`}
              >
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-6 space-y-6">
              
              {/* Question Box */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <h4 className={`text-xs font-semibold uppercase tracking-wider ${textSecondary} flex items-center gap-1.5`}>
                    <i className="ri-questionnaire-line text-blue-500"></i>
                    Question
                  </h4>
                  <button onClick={() => handleQaCopyField(editQuestionText || '', 'question')} className="text-xs text-blue-600 dark:text-blue-400 flex items-center gap-1 cursor-pointer bg-transparent border-none font-semibold hover:underline">
                    <i className={qaCopiedField === 'question' ? 'ri-check-line text-emerald-500' : 'ri-file-copy-line'} />
                    {qaCopiedField === 'question' ? 'Copied!' : 'Copy'}
                  </button>
                </div>
                <textarea 
                  value={editQuestionText}
                  onChange={(e) => setEditQuestionText(e.target.value)}
                  className={`w-full h-24 p-3 text-sm rounded-xl border focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none transition-all resize-none leading-snug
                    ${darkMode 
                      ? 'bg-gray-900 border-gray-700 text-gray-200 placeholder-gray-600' 
                      : 'bg-white border-gray-250 text-gray-800 placeholder-gray-400'
                    }`}
                  placeholder="Enter the full question..."
                />
              </div>

              {/* Answer Edit Box */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-3">
                    <h4 className={`text-xs font-semibold uppercase tracking-wider ${textSecondary} flex items-center gap-1.5`}>
                      <i className="ri-message-3-line text-emerald-500"></i>
                      Generated Answer
                    </h4>
                    <button onClick={() => handleQaCopyField(editAnswerText || '', 'answer')} className="text-xs text-blue-600 dark:text-blue-400 flex items-center gap-1 cursor-pointer bg-transparent border-none font-semibold hover:underline">
                      <i className={qaCopiedField === 'answer' ? 'ri-check-line text-emerald-500' : 'ri-file-copy-line'} />
                      {qaCopiedField === 'answer' ? 'Copied!' : 'Copy'}
                    </button>
                  </div>
                  <span className={`text-xs font-medium ${drawerAnswer.is_user_modified ? 'text-amber-600 dark:text-amber-400' : 'text-gray-500'}`}>
                    {drawerAnswer.is_user_modified ? '(Edited)' : '(AI Generated)'}
                  </span>
                </div>
                
                <textarea 
                  value={editAnswerText}
                  onChange={(e) => setEditAnswerText(e.target.value)}
                  className={`w-full h-80 p-4 text-sm rounded-xl border focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none transition-all resize-none leading-relaxed
                    ${darkMode 
                      ? 'bg-gray-900 border-gray-700 text-gray-200 placeholder-gray-600' 
                      : 'bg-white border-gray-200 text-gray-800 placeholder-gray-400'
                    }`}
                  placeholder="Write your answer here..."
                />
                
                <div className="flex items-center justify-between mt-3">
                  <div className="flex items-center gap-4">
                    <span className="text-xs text-gray-500 dark:text-gray-400">
                      <strong className="text-gray-700 dark:text-gray-300">{editAnswerText.split(/\s+/).filter(w => w.length > 0).length}</strong> words
                    </span>
                    <span className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1">
                      <i className="ri-key-2-line text-purple-500"></i>
                      Keyword: <strong className="text-gray-700 dark:text-gray-300">{drawerAnswer.keyword || '--'}</strong>
                    </span>
                  </div>
                </div>
              </div>
            </div>
            {/* Drawer Footer with Copy All and Save Changes */}
            <div className={`px-6 py-4 border-t ${borderColor} flex justify-between items-center bg-gray-50 dark:bg-gray-800/80`}>
              <button
                onClick={() => handleQaCopyAll(drawerAnswer)}
                className={`flex items-center gap-1.5 text-sm px-4 py-2 border rounded-lg transition-colors cursor-pointer ${darkMode ? 'border-gray-600 text-gray-300 hover:bg-gray-700' : 'border-gray-300 text-gray-600 hover:bg-gray-50'}`}
              >
                <i className={qaCopiedField === 'all' ? 'ri-check-double-line text-emerald-500' : 'ri-file-copy-line'} />
                {qaCopiedField === 'all' ? 'All Copied!' : 'Copy All'}
              </button>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setDrawerAnswer(null)}
                  className={`text-sm px-4 py-2 transition-colors cursor-pointer ${darkMode ? 'text-gray-400 hover:text-white' : 'text-gray-500 hover:text-gray-900'}`}
                >
                  Close
                </button>
                <button
                  onClick={saveDrawerEdit}
                  disabled={isSavingEdit || editAnswerText === (drawerAnswer.answer || '')}
                  className={`px-4 py-2 text-sm font-medium rounded-lg flex items-center gap-1.5 transition-all
                    ${isSavingEdit || editAnswerText === (drawerAnswer.answer || '')
                      ? 'bg-gray-100 text-gray-400 dark:bg-gray-800 dark:text-gray-500 cursor-not-allowed'
                      : 'bg-blue-600 hover:bg-blue-700 text-white shadow-sm hover:shadow cursor-pointer'
                    }`}
                >
                  {isSavingEdit ? (
                    <><i className="ri-loader-4-line animate-spin"></i> Saving...</>
                  ) : (
                    <><i className="ri-save-line"></i> Save Changes</>
                  )}
                </button>
              </div>
            </div>
          </div>
        </>
      )}

    </div>
  );
}
